/**
 * Calendar-area query/mutation hooks (Home + Calendar pages, per-day content
 * generation, dashboard stat). Follows src/queries/webhooks.js's template.
 *
 * useEventData is shared by Home, Calendar and Freetrial (the public demo
 * calendar) — all three called the exact same unauthenticated, param-less
 * GET /chat/event-data, so they now share one query key/cache entry instead
 * of each page refetching it independently.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";
import env from "../config/env";

const { GET, POST } = ReqMethods;

export const eventDataKeys = {
  all: ["eventData"],
};

export function useEventData() {
  return useQuery({
    queryKey: eventDataKeys.all,
    queryFn: async () => {
      const res = await request(GET, "/chat/event-data");
      return res?.data ?? null;
    },
  });
}

// Dashboard.tsx's "No of Chats" stat. Per earlier research the result
// (totalMatchLocationfromchat) is dead/unread — converted anyway for
// consistency with the rest of this migration, no special handling needed
// since nothing reads the value.
export const dashboardKeys = {
  all: ["dashboardLocationChatCount"],
  byLocation: (locationId) => [...dashboardKeys.all, locationId],
};

export function useDashboardLocationChatCount(locationId) {
  return useQuery({
    queryKey: dashboardKeys.byLocation(locationId),
    queryFn: async () => {
      const res = await request(GET, `/chat/get_data_by_location/${locationId}`);
      return res?.data?.length ?? 0;
    },
  });
}

// ---- components/day (authenticated calendar's per-event AI generation) ----
// Both are WRITES fired imperatively (onMouseEnter/onClick handlers deep in
// components/day/index.tsx's handleRequest/handleRequestfirstloadimageonly/
// translate_enabled/LoadOld_response), called via .mutateAsync() rather than
// rendered off `isPending` — that component tracks its own granular
// per-event loading state (loadingEventTitles/LoaderContent) and must keep
// doing so.

export function useGenerateImageFinal() {
  return useMutation({
    /** @param {Record<string, any>} payload */
    mutationFn: (payload) => request(POST, "/chat/generateImagefinal", { payload }),
  });
}

// env.API_ENV is interpolated directly into the path (not a query param) —
// preserved exactly from the original `${BASE_URL}/chat/${API_ENV}` calls.
export function useGenerateChatContent() {
  return useMutation({
    /** @param {Record<string, any>} payload */
    mutationFn: (payload) => request(POST, `/chat/${env.API_ENV}`, { payload }),
  });
}

// components/day's "Publish"/"Schedule" form submit (handlePosting), fired
// imperatively — not rendered off isPending, matches the mutations above.
export function usePostOnGhl() {
  return useMutation({
    /** @param {Record<string, any>} payload */
    mutationFn: (payload) => request(POST, "/chat/postonghl", { payload }),
  });
}

// ---- pages/calendar (config lookups, fired once on mount) ----
// All four are simple param-less GETs that populate the channel/tone/image/
// translate option dropdowns Day renders — was
// hookUsed.channeloptions.Get() etc. via useAppServices.

export function useChannelOptions() {
  return useQuery({
    queryKey: ["channelOptions"],
    queryFn: async () => {
      const res = await request(GET, "/channeloptions");
      return res?.data ?? [];
    },
  });
}

export function useToneOptions() {
  return useQuery({
    queryKey: ["toneOptions"],
    queryFn: async () => {
      const res = await request(GET, "/toneoptions");
      return res?.data ?? [];
    },
  });
}

export function useImgOptions() {
  return useQuery({
    queryKey: ["imgOptions"],
    queryFn: async () => {
      const res = await request(GET, "/imgoptions");
      return res?.data ?? [];
    },
  });
}

export function useTranslateOptions() {
  return useQuery({
    queryKey: ["translateOptions"],
    queryFn: async () => {
      const res = await request(GET, "/translateoption");
      return res?.data ?? [];
    },
  });
}
